<template>
    <div v-if="hasIssues" class="mt-4 space-y-4">
        <div
            v-if="mismatches.length > 0"
            class="border border-amber-300 bg-amber-50 rounded-md p-4"
        >
            <div class="flex items-start justify-between gap-4">
                <div>
                    <h3 class="text-sm font-bold text-amber-900">
                        {{ mismatches.length }} plan(s) rattaché(s) malgré un nom divergent
                    </h3>
                    <p class="text-xs text-amber-800 mt-1">
                        Les plans ont bien été importés, mais leur nom ne correspond pas à celui des
                        fichiers de zones. À faire corriger par le prestataire.
                    </p>
                </div>
                <button
                    type="button"
                    @click="copyReport"
                    class="shrink-0 px-3 py-1.5 text-xs font-medium rounded-md border border-amber-400 text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                >
                    {{ copyLabel }}
                </button>
            </div>

            <p class="text-xs text-amber-900 mt-3">
                <span class="font-medium">Motif :</span>
                <span v-for="(count, pattern) in mismatchPatterns" :key="pattern" class="ml-1">
                    <code class="bg-amber-100 px-1 rounded">{{ pattern }}</code> ({{ count }})
                </span>
            </p>

            <textarea
                v-if="copyFailed"
                ref="fallback"
                :value="report"
                readonly
                rows="4"
                class="mt-3 w-full text-xs font-mono border border-amber-300 rounded p-2 bg-white"
                aria-label="Rapport à copier manuellement"
            ></textarea>

            <div class="mt-3 max-h-48 overflow-auto">
                <table class="w-full text-xs">
                    <thead class="text-amber-900 text-left sticky top-0 bg-amber-50">
                        <tr>
                            <th class="py-1 pr-3 font-medium">Unité</th>
                            <th class="py-1 pr-3 font-medium">Type</th>
                            <th class="py-1 pr-3 font-medium">Attendu</th>
                            <th class="py-1 font-medium">Livré</th>
                        </tr>
                    </thead>
                    <tbody class="text-amber-800">
                        <tr v-for="(row, index) in mismatches" :key="index" class="border-t border-amber-200">
                            <td class="py-1 pr-3">{{ row.unit }}</td>
                            <td class="py-1 pr-3">{{ row.kind }}</td>
                            <td class="py-1 pr-3"><code>{{ row.expected }}</code></td>
                            <td class="py-1"><code>{{ row.delivered }}</code></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-if="missing.length > 0"
            class="border border-red-300 bg-red-50 rounded-md p-4"
        >
            <h3 class="text-sm font-bold text-red-900">
                {{ missing.length }} plan(s) introuvable(s)
            </h3>
            <p class="text-xs text-red-800 mt-1">
                Absents de la livraison, ou nommés de façon trop éloignée pour être rapprochés.
                Ces unités n'ont pas de plan.
            </p>

            <div class="mt-3 max-h-48 overflow-auto">
                <table class="w-full text-xs">
                    <thead class="text-red-900 text-left sticky top-0 bg-red-50">
                        <tr>
                            <th class="py-1 pr-3 font-medium">Bâtiment</th>
                            <th class="py-1 pr-3 font-medium">Étage</th>
                            <th class="py-1 pr-3 font-medium">Unité</th>
                            <th class="py-1 font-medium">Fichier attendu</th>
                        </tr>
                    </thead>
                    <tbody class="text-red-800">
                        <tr v-for="(row, index) in missing" :key="index" class="border-t border-red-200">
                            <td class="py-1 pr-3">{{ row.building }}</td>
                            <td class="py-1 pr-3">{{ row.floor }}</td>
                            <td class="py-1 pr-3">{{ row.unit }}</td>
                            <td class="py-1"><code>{{ row.expected }}</code></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="briefSettings.length > 0" class="border border-gray-300 bg-white rounded-md p-4">
            <h3 class="text-sm font-bold text-gray-900">
                Réglages lus du brief xlsx
                <span v-if="briefApplied.length > 0" class="ml-1 font-normal text-gray-600">
                    — {{ briefApplied.length }} appliqué(s), {{ briefReported.length }} à traiter à la main
                </span>
            </h3>
            <p class="text-xs text-gray-600 mt-1">
                En simulation, cette liste montre ce qui <em>serait</em> appliqué : rien n'est encore écrit.
            </p>

            <div class="mt-3 max-h-64 overflow-auto">
                <table class="w-full text-xs">
                    <thead class="text-gray-700 text-left sticky top-0 bg-white">
                        <tr>
                            <th class="py-1 pr-3 font-medium">Réglage</th>
                            <th class="py-1 pr-3 font-medium">Cible</th>
                            <th class="py-1 pr-3 font-medium">Actuel</th>
                            <th class="py-1 pr-3 font-medium">Brief</th>
                            <th class="py-1 font-medium">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(row, index) in briefSettings"
                            :key="index"
                            class="border-t border-gray-200"
                            :class="row.action === 'set' ? 'bg-green-50' : ''"
                        >
                            <td class="py-1 pr-3 text-gray-900">{{ row.label }}</td>
                            <td class="py-1 pr-3 text-gray-500"><code>{{ row.target }}</code></td>
                            <td class="py-1 pr-3 text-gray-500">{{ row.current }}</td>
                            <td class="py-1 pr-3 text-gray-900">{{ row.value }}</td>
                            <td class="py-1">
                                <span
                                    class="px-2 py-0.5 rounded-full whitespace-nowrap"
                                    :class="{
                                        'bg-green-100 text-green-800': row.action === 'set',
                                        'bg-gray-100 text-gray-600': row.action === 'unchanged' || row.action === 'ok',
                                        'bg-amber-100 text-amber-800': row.action.startsWith('report'),
                                    }"
                                >{{ row.action }}</span>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div
            v-if="missingCategories.length > 0"
            class="border border-gray-300 bg-gray-50 rounded-md p-4"
        >
            <h3 class="text-sm font-bold text-gray-900">
                Typologies absentes du projet : {{ missingCategories.join(', ') }}
            </h3>
            <p class="text-xs text-gray-600 mt-1">
                Les unités concernées ont été créées sans catégorie. Relancer un import en cochant
                « Créer les typologies absentes » pour les ajouter.
            </p>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        issues: Object,
        filename: String,
    },
    data() {
        return {
            copied: false,
            copyFailed: false,
        }
    },
    computed: {
        mismatches() {
            return this.issues?.naming_mismatches ?? [];
        },
        missing() {
            return this.issues?.missing_plans ?? [];
        },
        missingCategories() {
            return this.issues?.missing_categories ?? [];
        },
        briefSettings() {
            return this.issues?.brief_settings ?? [];
        },
        briefApplied() {
            return this.briefSettings.filter(row => row.action === 'set');
        },
        briefReported() {
            return this.briefSettings.filter(row => row.action.startsWith('report'));
        },
        hasIssues() {
            return this.mismatches.length > 0
                || this.missing.length > 0
                || this.missingCategories.length > 0
                || this.briefSettings.length > 0;
        },
        /**
         * Les écarts sont répétitifs : les chiffres sont masqués pour ne montrer que la règle
         * de nommage fautive ("BNNN.jpg -> BN NN.jpg"), qui est ce qu'on transmet au prestataire.
         */
        mismatchPatterns() {
            return this.mismatches.reduce((patterns, row) => {
                const key = row.expected.replace(/\d/g, 'N') + ' → ' + row.delivered.replace(/\d/g, 'N');

                patterns[key] = (patterns[key] || 0) + 1;

                return patterns;
            }, {});
        },
        copyLabel() {
            if (this.copied) {
                return 'Copié';
            }

            return this.copyFailed ? 'Copie impossible — sélectionnez le texte' : 'Copier le rapport';
        },
        report() {
            const lines = [`Livraison : ${this.filename}`, ''];

            if (this.mismatches.length > 0) {
                lines.push(`${this.mismatches.length} plan(s) au nom divergent de celui des fichiers de zones :`);
                Object.entries(this.mismatchPatterns).forEach(([pattern, count]) => {
                    lines.push(`  motif : ${pattern} (${count} fichiers)`);
                });
                lines.push('');
                this.mismatches.forEach(row => {
                    lines.push(`  ${row.unit} — ${row.kind} : attendu ${row.expected}, livré ${row.delivered}`);
                });
                lines.push('');
            }

            if (this.missing.length > 0) {
                lines.push(`${this.missing.length} plan(s) introuvable(s) :`);
                this.missing.forEach(row => {
                    lines.push(`  ${row.building} / ${row.floor} / ${row.unit} — attendu ${row.expected}`);
                });
            }

            return lines.join('\n');
        },
    },
    methods: {
        /**
         * navigator.clipboard n'existe qu'en HTTPS ou sur localhost : hors de ces contextes, le
         * rapport est affiché dans un champ présélectionné plutôt que de ne rien faire.
         */
        copyReport() {
            if (! navigator.clipboard) {
                this.revealFallback();

                return;
            }

            navigator.clipboard.writeText(this.report)
                .then(() => {
                    this.copied = true;
                    this.copyFailed = false;
                    setTimeout(() => { this.copied = false; }, 2000);
                })
                .catch(() => this.revealFallback());
        },
        revealFallback() {
            this.copyFailed = true;

            this.$nextTick(() => {
                if (this.$refs.fallback) {
                    this.$refs.fallback.focus();
                    this.$refs.fallback.select();
                }
            });
        },
    },
}
</script>
